import React, { useReducer, ReactNode } from 'react';
import Device from '../../models/device';
import { createContext } from 'use-context-selector';
import UserFetch, { UserPost } from '../../userFetch';
import { isLastHeartbeatStale } from '../../utils';

export interface DeviceStore extends Device {
  ongoingCommand?: boolean;
  lastHeartbeatStale?: boolean;
}

interface DeviceState {
  devices: Record<string, DeviceStore>;
  reloadNeeded: boolean;
  ongoingCommand: Record<string, string>;
}

type DeviceAction =
  | { type: 'SET_DEVICES'; payload: Device[] }
  | { type: 'RELOAD_DEVICES' }
  | { type: 'SENDING_COMMAND'; payload: { serial_number: string, command: string }[] }
  | { type: 'COMMAND_SENT'; payload: { serial_number: string }[] };

const initialState: DeviceState = {
  reloadNeeded: true,
  devices: {},
  ongoingCommand: {},
};

export const DevicesContext = createContext<any>(null);

const deviceReducer = (state: DeviceState, action: DeviceAction): DeviceState => {
  switch (action.type) {
    case 'SET_DEVICES':
      {
        const newState = {
          ...state,
          devices: mergeDevices(state.devices, action.payload),
          reloadNeeded: false,
        }

        Object.values(newState.devices).forEach((device) => {
          device.lastHeartbeatStale = isLastHeartbeatStale(device.last_heartbeat);
        });

        return newState;
      }
    case 'RELOAD_DEVICES':
      return { ...state, reloadNeeded: true };
    case 'SENDING_COMMAND':
      {
        action.payload.forEach((action) => {
          state.ongoingCommand[action.serial_number] = action.command;
        });
        return {
          ...state
        };
      }
    case 'COMMAND_SENT':
      {
        action.payload.forEach((action) => {
          delete state.ongoingCommand[action.serial_number];
        });

        return {
          ...state
        };
      }
    default:
      return state;
  }
};

export const DeviceProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(deviceReducer, initialState);

  const updateDevice = React.useCallback(async (payload: Partial<Device>) => {
    await UserPost('/api/devices', payload)
      .then(() => {
        dispatch({ type: 'RELOAD_DEVICES' });
      }).catch((error) => {
        console.error('Error updating device:', error);
      });
  }, []);

  const deleteDevice = React.useCallback(async (serialNumber: string) => {
    await UserFetch(`/api/devices/${serialNumber}`, {
      method: 'DELETE'
    }).then(() => {
      dispatch({ type: 'RELOAD_DEVICES' });
    }).catch((error) => {
      console.error('Error deleting device:', error);
    });
  }, []);

  return (
    <DevicesContext.Provider value={{
      state,
      dispatch,
      updateDevice,
      deleteDevice,
    }}>
      {children}
    </DevicesContext.Provider>
  );
};

function mergeDevices(devices: Record<string, DeviceStore>, newDevices: Device[]): Record<string, DeviceStore> {
  // Create a map of new devices for quick lookup
  const newDevicesRecord = newDevices.reduce((acc, device) => {
    acc[device.serial_number] = device;
    return acc;
  }, {} as Record<string, Device>);

  // Remove devices that are not in the new devices list
  Object.keys(devices).filter((serial_number) => {
    return !newDevices.find((newDevice) => newDevice.serial_number === serial_number);
  }).forEach((serial_number) => {
    delete devices[serial_number];
  });

  // Add or update devices in the devices list
  return newDevices.reduce((acc, device) => {
    if (!acc[device.serial_number]) {
      acc[device.serial_number] = device;
    } else {
      acc[device.serial_number] = Object.assign(device, newDevicesRecord[device.serial_number]);
    }
    return acc;
  }, {} as Record<string, DeviceStore>);
}
